import { trackEventAfterNavigation } from '~/composables/useMetaPixel';
import { getClientPromptSessionId } from '~/utils/clientMobileAppPrompt';
import {
   captureTouch,
   mergeAttribution,
   type AcquisitionAttribution,
   type AcquisitionStep,
   type ClientAcquisition,
} from '~/utils/acquisition';

const STORAGE_KEY = 'talktime.acquisition.v1';

export const useAcquisition = () => {
   const attribution = useState<AcquisitionAttribution>('acquisition-attribution', () => ({}));
   const clientAcquisition = useState<ClientAcquisition | null>('client-acquisition', () => null);
   const acquisitionAccount = useState<string | null>('acquisition-account', () => null);
   const pendingAccount = useState<string | null>('acquisition-pending-account', () => null);
   const completed = useState<string[]>('acquisition-stages', () => []);
   const { clientToken, isClientAuthenticated } = useAuthClientStorage();
   const account = () => getClientPromptSessionId(clientToken.value?.access_token);

   const persistAttribution = () => {
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(attribution.value)); } catch { /* Storage can be unavailable. */ }
   };
   const importAttribution = (incoming?: AcquisitionAttribution) => {
      if (!incoming) return;
      attribution.value = mergeAttribution(attribution.value, incoming);
      persistAttribution();
   };
   const captureVisit = (url: URL, referrer = '') => {
      let stored: AcquisitionAttribution = {};
      try { stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); } catch { /* Ignore invalid browser state. */ }
      attribution.value = mergeAttribution({}, stored);
      const touch = captureTouch(url, referrer);
      const isCampaign = Object.keys(touch.utm).length > 0 || Object.keys(touch.click_ids).length > 0;
      const previous = attribution.value.last_touch;
      const isNewCampaign = isCampaign && (
         JSON.stringify(previous?.utm) !== JSON.stringify(touch.utm) ||
         JSON.stringify(previous?.click_ids) !== JSON.stringify(touch.click_ids)
      );
      importAttribution({
         first_touch: attribution.value.first_touch || touch,
         last_touch: !previous || isNewCampaign ? touch : previous,
      });
   };
   const synchronizeAccount = () => {
      const current = account();
      if (current !== acquisitionAccount.value) {
         clientAcquisition.value = null;
         completed.value = [];
         acquisitionAccount.value = current;
      }
      return current;
   };
   const refreshClientAcquisition = async () => {
      const current = synchronizeAccount();
      if (!current || !isClientAuthenticated.value) return;
      const { data, error } = await useIFetch<ClientAcquisition>('/client/acquisition', {
         key: `client-acquisition-${current}`,
      });
      if (current !== account()) return;
      if (!error.value && data.value) {
         clientAcquisition.value = data.value;
      } else {
         clientAcquisition.value = null;
      }
      return clientAcquisition.value;
   };
   const activePromoCode = computed(() => {
      if (account() !== acquisitionAccount.value) return;
      if (clientAcquisition.value?.status === 'paid' || clientAcquisition.value?.status === 'deleted') return;
      const promo = clientAcquisition.value?.promo;
      if (promo?.status !== 'active') return;
      return promo.code;
   });
   const trackClientStep = async (step: AcquisitionStep, complete = false) => {
      if (step !== 'pricing_viewed') return;
      // Plans seen. Called from route middleware, so the pixel waits for the page.
      trackEventAfterNavigation('ViewContent');
      const current = synchronizeAccount();
      const stage = `${current}:${step}:${complete}`;
      if (!current || completed.value.includes(stage)) return;
      const { error } = await useIFetch('/client/acquisition/progress', {
         method: 'POST',
         body: { step, completed: complete },
         key: `acquisition-progress-${stage}`,
      });
      if (!error.value && current === account()) completed.value.push(stage);
   };
   const ensureClientAcquisition = async () => {
      const current = synchronizeAccount();
      if (!current || !isClientAuthenticated.value || pendingAccount.value === current) return;
      pendingAccount.value = current;
      try { await refreshClientAcquisition(); } finally {
         if (pendingAccount.value === current) pendingAccount.value = null;
      }
   };
   return {
      attribution, clientAcquisition, activePromoCode, captureVisit, importAttribution,
      refreshClientAcquisition, ensureClientAcquisition, trackClientStep,
   };
};
